import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const MIN_ANSWER = 20;

export default function HandsOnExercise({ moduleTitle, moduleId, exercise, onComplete }) {
  const { t } = useTranslation();
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (!exercise || !exercise.tasks || exercise.tasks.length === 0) {
    return null;
  }

  const currentTask = exercise.tasks[currentTaskIndex];
  const isLastTask = currentTaskIndex === exercise.tasks.length - 1;
  const answered = (idx) => (answers[idx]?.trim().length || 0) >= MIN_ANSWER;
  const allAnswered = exercise.tasks.every((_, idx) => answered(idx));

  const handleAnswerChange = (value) => {
    setSubmitError(null);
    setAnswers(prev => ({
      ...prev,
      [currentTaskIndex]: value
    }));
  };

  const handleNextTask = () => {
    if (currentTaskIndex < exercise.tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    }
  };

  const handlePrevTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const handleDownloadAnswers = () => {
    const timestamp = new Date().toLocaleString();
    const fileName = `${moduleTitle.replace(/\s+/g, '_')}_Answers_${Date.now()}.txt`;

    let content = `${moduleTitle} - Hands-On Exercise Answers\n`;
    content += `Downloaded: ${timestamp}\n`;
    content += `\n${'='.repeat(60)}\n\n`;

    exercise.tasks.forEach((task, idx) => {
      content += `Task ${idx + 1}: ${task.title}\n`;
      content += `-${'-'.repeat(50)}\n`;
      content += `Question: ${task.question}\n\n`;
      content += `Your Answer:\n${answers[idx] || '(No answer provided)'}\n`;
      content += `\n${'='.repeat(60)}\n\n`;
    });

    // Create and download text file
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(content));
    element.setAttribute('download', fileName);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleSubmit = async () => {
    const missing = exercise.tasks.map((_, idx) => idx).filter(idx => !answered(idx));
    if (missing.length) {
      setSubmitError(`Task${missing.length > 1 ? 's' : ''} ${missing.map(i => i + 1).join(', ')} need${missing.length > 1 ? '' : 's'} an answer of at least ${MIN_ANSWER} characters.`);
      setCurrentTaskIndex(missing[0]);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      // Submit answers
      const payload = {
        moduleTitle,
        exercise: exercise.id,
        answers: exercise.tasks.map((task, idx) => ({
          taskId: task.id,
          question: task.question,
          answer: answers[idx] || ''
        }))
      };

      // Keep the answers as training evidence
      const res = await fetch('/api/training/exercise-answers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId: moduleId || moduleTitle, ...payload })
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Could not save your answers');
      }

      setSubmitted(true);
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (error) {
      console.error('Error submitting hands-on:', error);
      setSubmitError(error.message || 'Error submitting your answers');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#e8f5e9',
        borderRadius: '8px',
        textAlign: 'center',
        marginTop: '20px'
      }}>
        <h3 style={{ color: '#2e7d32', marginTop: 0 }}>✅ Hands-On Exercise Complete!</h3>
        <p>Great job completing the practical exercises. Your answers have been recorded.</p>
        <p style={{ fontSize: '12px', color: '#666' }}>Moving on...</p>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '30px',
      padding: '20px',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      border: '2px solid #ff9800'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 0 0', color: '#ff9800' }}>🛠 Hands-On Exercise</h3>
        <span style={{ fontSize: '12px', color: '#666' }}>
          Task {currentTaskIndex + 1} of {exercise.tasks.length}
        </span>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '6px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginTop: 0, color: '#333' }}>
          {currentTask.title}
        </h4>

        <div style={{ marginBottom: '20px', lineHeight: '1.6', color: '#555' }}>
          <p><strong>📋 {t('handsOn.instructionsLabel')}</strong></p>
          <p>{currentTask.instructions}</p>

          {currentTask.crossregguardLink && (
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '12px',
              borderRadius: '4px',
              marginTop: '15px',
              marginBottom: '15px'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>🔗 {t('handsOn.useCrossRegGuardLabel')}</strong>
              </p>
              <a
                href={currentTask.crossregguardLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                {t('handsOn.openCrossRegGuardButton')} →
              </a>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            ❓ {currentTask.question}
          </label>
          <textarea
            value={answers[currentTaskIndex] || ''}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder={t('handsOn.answerPlaceholder')}
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontFamily: 'sans-serif',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          <p style={{ fontSize: '12px', color: '#999', margin: '8px 0 0 0' }}>
            {answers[currentTaskIndex]?.trim().length || 0} characters{!answered(currentTaskIndex) ? ` (minimum ${MIN_ANSWER})` : ''}
          </p>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '10px',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <button
            onClick={handlePrevTask}
            disabled={currentTaskIndex === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: currentTaskIndex === 0 ? '#ccc' : '#999',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentTaskIndex === 0 ? 'not-allowed' : 'pointer',
              marginRight: '10px'
            }}
          >
            ← Previous
          </button>
          {!isLastTask && (
            <button
              onClick={handleNextTask}
              disabled={!answered(currentTaskIndex)}
              style={{
                padding: '10px 20px',
                backgroundColor: !answered(currentTaskIndex) ? '#ccc' : '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: !answered(currentTaskIndex) ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {Object.values(answers).some(a => a?.trim()) && (
            <button
              onClick={handleDownloadAnswers}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
              title="Download your answers as a text file"
            >
              ⬇️ Download Answers
            </button>
          )}

          {isLastTask && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              title={!allAnswered ? `Every task needs at least ${MIN_ANSWER} characters` : ''}
              style={{
                padding: '12px 30px',
                backgroundColor: submitting ? '#ccc' : allAnswered ? '#4caf50' : '#81c784',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              {submitting ? '⏳ Submitting...' : '✅ Submit All Answers'}
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <div role="alert" style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '6px', fontSize: '14px' }}>
          {submitError}
        </div>
      )}

      <div style={{
        marginTop: '15px',
        display: 'flex',
        gap: '5px',
        justifyContent: 'center'
      }}>
        {exercise.tasks.map((_, idx) => (
          <div
            key={idx}
            onClick={() => setCurrentTaskIndex(idx)}
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              backgroundColor: answered(idx) ? '#4caf50' : answers[idx]?.trim() ? '#f59e0b' : '#ddd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold',
              border: idx === currentTaskIndex ? '3px solid #ff9800' : 'none'
            }}
          >
            {answered(idx) ? '✓' : idx + 1}
          </div>
        ))}
      </div>
    </div>
  );
}
